"use client";

import Link from 'next/link';
import Image from 'next/image';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import type { CaseStudy } from '../../lib/types';
import CTAButton from '../ui/CTAButton';
import CursorTooltip from '../ui/CursorTooltip';
import { trackEvent } from '@/lib/analytics';
import { useInView } from '@/lib/useInView';

// Component for the "More of our work" button
function MoreWorkButton({ onToggle }: { onToggle: () => void }) {
  return (
    <div className="text-center">
      <CTAButton onClick={onToggle}>
        More of our work
      </CTAButton>
    </div>
  );
}

// Component for thumbnail media with video-on-hover support
function ThumbnailMedia({ 
  thumbnail, 
  thumbnailVideo, 
  title,
  isHovered
}: { 
  thumbnail: string; 
  thumbnailVideo?: string; 
  title: string;
  isHovered: boolean;
}) {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = thumbnailVideo && !videoError;

  const handleVideoError = () => {
    setVideoError(true);
  };

  // Control video playback based on hover state
  useEffect(() => {
    if (!videoRef.current || !hasVideo) return;
    
    if (isHovered) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, fail silently
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to beginning
    }
  }, [isHovered, hasVideo]);

  return (
    <>
      {/* Static image - always rendered as base layer */}
      <Image
        src={thumbnail}
        alt={title}
        fill
        className={`object-cover transition-transform duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105 rounded-3xl ${hasVideo ? 'z-0' : 'z-10'}`}
        sizes="(min-width: 768px) 50vw, 100vw"
      />
      
      {/* Video layer - only rendered if video exists, plays on hover */}
      {hasVideo && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          onError={handleVideoError}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105 rounded-3xl z-10 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          aria-label={`${title} thumbnail video`}
        >
          <source src={thumbnailVideo} type="video/mp4" />
        </video>
      )}
    </>
  );
}

// Component for individual case study card with animate-in-view
function CaseStudyCard({ 
  caseStudy, 
  idx, 
  onCardClick 
}: { 
  caseStudy: CaseStudy; 
  idx: number; 
  onCardClick: (title: string, url: string) => void;
}) {
  const { ref } = useInView<HTMLDivElement>();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  return (
    <div ref={ref} className="animate-in-view">
      <CursorTooltip
        tooltipText={CTA_TEXT_MAP[caseStudy.slug] || DEFAULT_CTA_TEXT}
        backgroundColor="#AAFA96"
        textColor="#000000"
      >
        <Link
          href={`/case-studies/${caseStudy.slug}`}
          onClick={() => onCardClick(caseStudy.title, `/case-studies/${caseStudy.slug}`)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleMouseEnter}
          onBlur={handleMouseLeave}
          className={`group lg:gap-8 flex flex-col lg:flex-row ${
            idx % 2 === 1 ? "lg:flex-row-reverse" : ""
          } rounded-none overflow-hidden`}
        >
          {/* Image Side */}
          <div className="lg:w-1/2 w-full flex-shrink-0 bg-gray-200 overflow-hidden relative aspect-video rounded-3xl">
            <ThumbnailMedia
              thumbnail={caseStudy.featuredImage}
              thumbnailVideo={caseStudy.heroVideo}
              title={caseStudy.title}
              isHovered={isHovered}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] rounded-3xl z-20" />
          </div>
          {/* Text Side */}
          <div className={`lg:w-1/2 w-full self-stretch ${
            idx % 2 === 1 ? "lg:flex lg:justify-end" : "lg:flex lg:justify-start"
          }`}>
            <div className={`lg:px-6 w-full h-full flex flex-col pt-[0.625rem] max-w-[28.625rem] ${
              idx % 2 === 1 ? "lg:text-right" : ""
            }`}>
              {/* <h3 className="heading-3 mb-5"> */}
              <h3 className="heading-3 mb-[0.625rem] lg:mb-[1.25rem]">
                {caseStudy.title}
              </h3>
              <p className="body-sm">
                {caseStudy.description}
              </p>
              {/* Mobile-only CTA */}
              <span className="lg:hidden body-default underline decoration-red-600 decoration-2 underline-offset-4 mt-3">
                {CTA_TEXT_MAP[caseStudy.slug] || DEFAULT_CTA_TEXT}
              </span>
            </div>
          </div>
        </Link>
      </CursorTooltip>
    </div>
  );
}

type CaseStudiesHomeProps = {
  featuredCaseStudies: CaseStudy[];
};

// Hardcoded CTA text per case study
const CTA_TEXT_MAP: Record<string, string> = {
  'american-airlines': 'Dive Deeper',
  'brooklyn-film-festival': 'Experience it',
  'google-fitbit': 'Get the story',
  'house-of-suntory': 'Explore the work',
  'staybl': 'See the impact',
};

const DEFAULT_CTA_TEXT = 'Dive Deeper';

// Container component for list view with staggered animation
function ListContainer({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // If reduced motion, immediately add animate class
      containerRef.current.classList.add('animate');
    } else {
      // Trigger animation on mount with delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          containerRef.current?.classList.add('animate');
        });
      });
    }
  }, []);
  
  return (
    <div ref={containerRef} className="animate-stagger-items relative flex flex-col gap-6 lg:gap-14 mt-16">
      {children}
    </div>
  );
}

export default function CaseStudiesHome({ featuredCaseStudies }: CaseStudiesHomeProps) {
  const pathname = usePathname();
  const [view, setView] = useState<'cards' | 'list'>('cards');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = (slug: string) => {
    setHoveredItem(slug);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  // Helper function to track homepage card clicks
  const handleCardClick = (clickText: string, clickUrl: string) => {
    trackEvent('hp_click', {
      click_text: clickText,
      click_url: clickUrl
    });
  };

  // In list view, always show all case studies; in cards view, respect the Show More toggle
  const displayedCaseStudies =
    view === 'list'
      ? featuredCaseStudies
      : showAll
        ? featuredCaseStudies
        : featuredCaseStudies.slice(0, 2);

  // Find the hovered case study for the floating image (uses hero image)
  const hoveredCaseStudy = useMemo(() => {
    if (!hoveredItem) return null;
    return featuredCaseStudies.find(cs => cs.slug === hoveredItem) || null;
  }, [hoveredItem, featuredCaseStudies]);

  if (featuredCaseStudies.length === 0) {
    return null;
  }

  return (
    // <section className="featured-work py-20 global-padding case-studies-home">
    // <section className="featured-work py-4 lg:py-20 global-padding case-studies-home">
    <section className="featured-work global-padding case-studies-home">
      {/* <div className="max-w-8xl mx-auto" ref={containerRef} onMouseMove={handleMouseMove}> */}
      <div className="" ref={containerRef} onMouseMove={handleMouseMove}>
        <div className="pb-4 lg:pb-[1.5rem] flex items-center justify-between gap-4 border-b border-black">
          <h2 className="heading-2">Our work</h2>
          <div className="inline-flex items-center gap-2" role="group" aria-label="View options">
            <button
              type="button"
              aria-pressed={view === 'cards'}
              aria-label="Card view"
              onClick={() => {
                trackEvent('hp_click', {
                  click_text: 'Grid',
                  page_path: pathname
                });
                setView('cards');
              }}
              className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
            >
              <span className="inline-flex items-center justify-center" aria-hidden="true">
                <Image
                  src={view === 'cards' ? '/images/work-icon-selected.png' : '/images/work-icon-default.png'}
                  alt=""
                  width={40}
                  height={40}
                />
              </span>
            </button>
            <button
              type="button"
              aria-pressed={view === 'list'}
              aria-label="List view"
              onClick={() => {
                trackEvent('hp_click', {
                  click_text: 'List',
                  page_path: pathname
                });
                setView('list');
              }}
              className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
            >
              <span className="inline-flex items-center justify-center" aria-hidden="true">
                <Image
                  src={view === 'list' ? '/images/list-icon-selected.png' : '/images/list-icon-default.png'}
                  alt=""
                  width={40}
                  height={40}
                />
              </span>
            </button>
          </div>
        </div>

        
        {view === 'cards' ? (
          <div className="flex flex-col gap-[3.625rem] lg:gap-20 mt-[3.75rem] lg:mt-[6.25rem] mb-[1.875rem] lg:mb-[2rem]">
            {displayedCaseStudies.map((caseStudy, idx) => (
              <CaseStudyCard
                key={caseStudy.slug}
                caseStudy={caseStudy}
                idx={idx}
                onCardClick={handleCardClick}
              />
            ))}
          </div>
        ) : (
          <>
            <ListContainer>
              {displayedCaseStudies.map((caseStudy) => (
                <Link
                  key={caseStudy.slug}
                  href={`/case-studies/${caseStudy.slug}`}
                  onClick={() => handleCardClick(caseStudy.title, `/case-studies/${caseStudy.slug}`)}
                  className="stagger-item group flex items-center justify-between px-2"
                  onMouseEnter={() => handleMouseEnter(caseStudy.slug)}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="hidden lg:block card-title-lg">
                    {caseStudy.title}
                  </span>
                  <span className="text-left lg:text-right">
                    <span className="block card-title-lg lg:hidden">
                      {caseStudy.title}
                    </span>
                    <span className="block body-sm-md-lg">
                      {caseStudy.category}
                    </span>
                  </span>
                </Link>
              ))}
            </ListContainer>
            
            {/* Floating Image - Shows hero image from case study */}
            {hoveredCaseStudy && (() => {
              // Check if mouse is 75% from the right of the page
              const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
              const imageWidth = 300; // 18.75rem = 300px
              const isNearRightEdge = mousePosition.x > windowWidth * 0.75;
              
              // If near right edge, position image to the left of cursor
              const leftPosition = isNearRightEdge 
                ? mousePosition.x - imageWidth - 20
                : mousePosition.x + 20;
              
              return (
                <div
                  className="fixed pointer-events-none z-50 transition-opacity duration-200"
                  style={{
                    left: leftPosition,
                    top: mousePosition.y - 100,
                    transform: 'translateY(-50%)'
                  }}
                >
                  <div className="w-[18.75rem] h-[12.5rem] relative rounded-lg overflow-hidden">
                    <Image
                      src={hoveredCaseStudy.featuredImage}
                      alt={hoveredCaseStudy.title}
                      fill
                      className="object-cover"
                      sizes="16rem"
                    />
                  </div>
                </div>
              );
            })()}
          </>
        )}
        
        {view !== 'list' && !showAll && (
          <MoreWorkButton onToggle={toggleShowAll} />
        )}
      </div>
    </section>
  );
}
